import { type CountryCode } from 'libphonenumber-js';
import { z } from 'zod';

import { BlockValueStringSchema } from './blockValueStringSchema.js';
import {
  DEFAULT_ALLOWED_COUNTRIES,
  buildInvalidPhoneNumberLabel,
  parseAllowedPhoneNumber,
} from './phoneNumberCountries.js';

interface BlockValueUsPhoneNumberSchemaArgs {
  isRequired?: boolean;
  /**
   * Country codes accepted for the phone number. Defaults to `['US']`.
   */
  allowedCountries?: CountryCode[];
}

/**
 * A Zod schema for validating phone numbers with configurable country allowlists. The function name
 * retains "Us" for backward compatibility and stable imports despite accepting non-US numbers when
 * configured via `allowedCountries`.
 *
 * @arg isRequired - if isRequired is false, the empty string is allowed. Chain the schema with .optional() if
 * the entire config property is optional.
 * @arg allowedCountries - country codes the value is allowed to belong to. Defaults to US-only.
 * @returns A Zod schema for validating values that are either block value templates or a phone number string
 *   belonging to one of the allowed countries.
 */
export function BlockValueUsPhoneNumberSchema(args: BlockValueUsPhoneNumberSchemaArgs) {
  const { isRequired = false, allowedCountries = DEFAULT_ALLOWED_COUNTRIES } = args;
  const errorMessage = `${buildInvalidPhoneNumberLabel(allowedCountries)}.`;
  const blockValueOrPhoneNumberSchema = BlockValueStringSchema({
    stringValidationSchema: z.string().superRefine((value, context) => {
      if (!parseAllowedPhoneNumber(value, allowedCountries)) {
        context.addIssue({
          code: z.ZodIssueCode.custom,
          message: errorMessage,
        });
      }
    }),
  });
  if (isRequired) {
    return blockValueOrPhoneNumberSchema;
  }
  return z.union([z.literal(''), blockValueOrPhoneNumberSchema]);
}
